/**
 * Input step editor v2 — what each read in an Input step is, in words.
 *
 * An Input step stores its reads as `selectedResources` entries
 * (InputResourcesSelector in ResourceForms.jsx writes them; codegen reads them):
 *   - `r-price`                a plain variable, or a char[] read whole (text)
 *   - `r-cmd[]`                legacy whole char[] (text)
 *   - `r-marks[i]`             one position of a list
 *   - `r-grid[r][c]`           one cell of a 2-D list
 *   - `student.roll`           a record field, keyed by NAME
 *   - `students[i].gpa`        a field of one record in a list of records
 *   - `r-student` (a record)   legacy "every field"
 * plus `readModes[resId]` = 'line' for a text read of the whole line.
 *
 * Pure: no store, no React. Nothing here writes a node; the editor builds new
 * entries with buildReadEntry, in exactly the shapes above, so codegen sees
 * what the old editor would have written.
 */
import { isBlockPointer, pointeeOf } from './memoryStep';

export const KIND_WORDS = {
  whole: 'whole number',
  decimal: 'decimal',
  character: 'character',
  text: 'text',
  yesno: 'yes-no',
};

// The C type each single-value kind declares (same as InputInlineAddForm).
export const KIND_DATA_TYPE = { whole: 'int', decimal: 'float', character: 'char', yesno: 'bool' };

const lc = (s) => String(s || '').toLowerCase().trim();
const rtOf = (r) => lc(r && (r.resourceType || r.type));

const isTypeDef = (r) => ['struct_type', 'union_type'].includes(rtOf(r));

/** A struct/union type by name, with the `struct_` prefix variants. */
export function findRecordType(structName, resources = []) {
  if (!structName) return null;
  const name = String(structName).replace(/^struct[ _]/, '');
  return (resources || []).find((r) => isTypeDef(r)
    && (r.name === structName || r.name === name || r.name === `struct_${name}`)) || null;
}

/** The record type a resource (instance, or list of records) holds, or null. */
export function recordTypeOf(resource, resources = []) {
  if (!resource) return null;
  const candidates = [
    resource.structTypeRef,
    resource.elementType,
    resource.dataType,
  ].filter((s) => typeof s === 'string' && s);
  for (const c of candidates) {
    const def = findRecordType(c, resources);
    if (def) return def;
  }
  return null;
}

/** The kind of value a C data type holds, or null when it is not a plain value. */
export function kindOfDataType(dataType, resources = []) {
  const dt = lc(dataType);
  if (!dt) return null;
  if (/char\s*(\[|\*)/.test(dt) || dt === 'string' || dt === 'str') return 'text';
  if (dt === 'char' || dt === 'unsigned char' || dt === 'signed char') return 'character';
  if (['float', 'double', 'long double'].includes(dt)) return 'decimal';
  if (['bool', '_bool', 'boolean'].includes(dt)) return 'yesno';
  if (/^(unsigned |signed )?(short|int|long|long long|long int|short int|long long int|unsigned)$/.test(dt)
      || ['size_t', 'int8_t', 'int16_t', 'int32_t', 'int64_t', 'uint8_t', 'uint16_t', 'uint32_t', 'uint64_t'].includes(dt)) {
    return 'whole';
  }
  // An enum is read as its number.
  if ((resources || []).some((r) => rtOf(r) === 'enum_type' && lc(r.name) === dt)) return 'whole';
  return null;
}

/** Every leaf field path below a record type (`roll`, `addr.city`), with its member. */
export function recordLeafFields(def, resources = [], prefix = '', seen = new Set()) {
  const out = [];
  if (!def || !Array.isArray(def.structMembers)) return out;
  const key = def.id || def.name;
  if (seen.has(key)) return out;
  const nextSeen = new Set(seen).add(key);
  for (const m of def.structMembers) {
    if (!m || !m.name) continue;
    const path = prefix ? `${prefix}.${m.name}` : m.name;
    const nested = (m.memberType === 'struct' || m.structTypeRef)
      ? findRecordType(m.structTypeRef || m.structRef || m.dataType, resources)
      : findRecordType(m.dataType, resources);
    if (nested) {
      out.push(...recordLeafFields(nested, resources, path, nextSeen));
    } else {
      out.push({ path, member: m });
    }
  }
  return out;
}

/** The kind of one record member. */
export function kindOfMember(member, resources = []) {
  if (!member) return null;
  const mt = lc(member.memberType);
  const dt = lc(member.arrayElementType || member.dataType);
  if (mt === 'array' || mt === 'array_1d' || mt === 'array_2d') {
    return dt === 'char' ? 'text' : null;
  }
  if (mt === 'pointer') return null;
  return kindOfDataType(member.dataType, resources);
}

/** The member at a dotted path below a record type, or null. */
export function memberAtPath(def, path, resources = []) {
  const leaf = recordLeafFields(def, resources).find((f) => f.path === path);
  return leaf ? leaf.member : null;
}

/** `marks[i]` → ['i']; `grid[r][c]` → ['r', 'c']; `students[i].gpa` → ['i']. */
export const entryIndices = (entry) => {
  const head = String(entry || '').split('.')[0];
  return [...head.matchAll(/\[([^\]]*)\]/g)].map((m) => m[1].trim());
};

/** `students[i].gpa` → 'students'; `r-marks[i]` → 'r-marks'. */
export const entryRoot = (entry) => String(entry || '').split('.')[0].split('[')[0].trim();

/** `students[i].addr.city` → 'addr.city'; '' when there is no field. */
export const entryField = (entry) => {
  const s = String(entry || '');
  const dot = s.indexOf('.');
  return dot < 0 ? '' : s.slice(dot + 1);
};

/** Is this resource something an Input can read? (the old selector's filter) */
export function isReadableResource(r) {
  const rt = rtOf(r);
  if (!r || !r.name) return false;
  if (rt === 'struct_type' || rt === 'union_type' || rt === 'enum_type') return false;
  if (rt === 'formula' || rt === 'constant' || rt === 'macro') return false;
  return true;
}

/**
 * What kind of thing a resource is, for the form:
 *   { shape: 'plain'|'text'|'list'|'grid'|'record'|'recordList'|'other', kind }
 * `kind` is the kind of ONE value read (a list's element kind).
 */
export function resourceShape(resource, resources = []) {
  if (!resource) return { shape: 'other', kind: null };
  const rt = rtOf(resource);
  const elem = resource.elementType || resource.dataType;
  const record = recordTypeOf(resource, resources);
  if (rt === 'array_2d') return { shape: 'grid', kind: kindOfDataType(elem, resources) };
  if (rt === 'array_1d' || rt === 'array') {
    if (lc(elem) === 'char') return { shape: 'text', kind: 'text' };
    if (record) return { shape: 'recordList', kind: null, record };
    return { shape: 'list', kind: kindOfDataType(elem, resources) };
  }
  // Memory got while the flow runs (a Memory step: p = malloc(n * sizeof(int)))
  // is read like a list: p[i]. A char pointer stays as it was (it may be text).
  if (rt === 'pointer' && isBlockPointer(resource) && lc(pointeeOf(resource)) !== 'char') {
    const pointee = pointeeOf(resource);
    if (/^(struct|union) /.test(pointee)) {
      const rec = findRecordType(pointee.replace(/^(struct|union) /, ''), resources) || findRecordType(pointee, resources);
      return rec ? { shape: 'recordList', kind: null, record: rec } : { shape: 'other', kind: null };
    }
    return { shape: 'list', kind: kindOfDataType(pointee, resources) };
  }
  if (record || rt === 'struct_instance' || rt === 'union_instance') return { shape: 'record', kind: null, record };
  if (rt === 'variable' || rt === '') {
    const kind = kindOfDataType(resource.dataType, resources);
    return { shape: kind === 'text' ? 'text' : 'plain', kind };
  }
  return { shape: 'other', kind: kindOfDataType(resource.dataType, resources) };
}

/**
 * One read, described. Returns
 *   { entry, resource, name, shape, kind, indices, field, path, lineMode, fields? }
 * shape: 'plain' | 'text' | 'element' | 'element2d' | 'field' | 'record' | 'unknown'
 */
export function describeRead(entry, resources = [], readModes = {}) {
  const raw = typeof entry === 'string' ? entry : String((entry && (entry.id || entry.name)) || '');
  const root = entryRoot(raw);
  const resource = (resources || []).find((r) => r && (r.id === root || r.name === root)) || null;
  const name = resource ? resource.name : root;
  const indices = entryIndices(raw);
  const field = entryField(raw);
  const base = { entry: raw, resource, name, indices, field, lineMode: false };
  if (!resource) return { ...base, shape: 'unknown', kind: null, path: raw };

  const { shape, kind, record } = resourceShape(resource, resources);
  if (field) {
    const def = record || recordTypeOf(resource, resources);
    const member = def ? memberAtPath(def, field, resources) : null;
    const idx = indices.length ? `[${indices.join('][')}]` : '';
    return { ...base, shape: 'field', kind: kindOfMember(member, resources), path: `${name}${idx}.${field}` };
  }
  if (shape === 'text' && indices.every((i) => i === '')) {
    return { ...base, shape: 'text', kind: 'text', path: name, lineMode: (readModes || {})[resource.id] === 'line' };
  }
  if (shape === 'record' || shape === 'recordList') {
    const def = record;
    const fields = def ? recordLeafFields(def, resources).map((f) => ({ path: f.path, kind: kindOfMember(f.member, resources) })) : [];
    return { ...base, shape: 'record', kind: null, path: name, fields };
  }
  if (shape === 'grid' || indices.length >= 2) {
    return { ...base, shape: 'element2d', kind: shape === 'text' ? 'character' : kind, path: name };
  }
  if (shape === 'list' || indices.length === 1) {
    return { ...base, shape: 'element', kind: shape === 'text' ? 'character' : kind, path: name };
  }
  return { ...base, shape: 'plain', kind, path: name };
}

/** Every read of a node, described, in order. */
export function describeReads(node, resources = []) {
  return (node?.selectedResources || []).map((e) => describeRead(e, resources, node?.readModes || {}));
}

/** The words for a kind ('whole number'), or '' when unknown. */
export const kindWords = (kind) => KIND_WORDS[kind] || '';

/**
 * The entry a read stores, in the shapes the old editor writes.
 *   plain / text           → id
 *   element                → `${id}[${i}]`
 *   element2d              → `${id}[${r}][${c}]`
 *   field                  → `${name}.${field}` / `${name}[${i}].${field}`
 */
export function buildReadEntry({ resource, shape, indices = [], field = '' }) {
  if (!resource) return '';
  const idx = indices.map((i) => `[${String(i ?? '').trim()}]`).join('');
  if (shape === 'field') return `${resource.name}${idx}.${field}`;
  if (shape === 'element' || shape === 'element2d') return `${resource.id}${idx}`;
  return resource.id;
}

/**
 * The resource fields a kind change writes, for a plain value, text, or a
 * list's element kind. Null when the change is not one this editor makes.
 */
export function kindChangeUpdates(resource, shape, kind, { textLetters = 50 } = {}) {
  if (!resource) return null;
  if (shape === 'element' || shape === 'element2d') {
    const dt = KIND_DATA_TYPE[kind];
    return dt ? { dataType: dt, elementType: dt } : null;
  }
  if (shape === 'plain' || shape === 'text') {
    if (kind === 'text') {
      if (shape === 'text') return null;
      return { resourceType: 'array_1d', dataType: 'char', elementType: 'char', size: String(Number(textLetters) + 1) };
    }
    const dt = KIND_DATA_TYPE[kind];
    if (!dt) return null;
    return shape === 'text'
      ? { resourceType: 'variable', dataType: dt, elementType: undefined, size: undefined }
      : { dataType: dt };
  }
  return null;
}
